import { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react'; // useLayoutEffect used in AddSubtaskRow
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ChevronDown,
  ListTree,
  Sparkles,
  CircleDashed,
  ShieldAlert,
  Zap,
  Folder,
  ListTodo,
  Archive,
  Hash,
  ArrowUpDown,
  Plus,
  CornerDownRight,
  SlidersHorizontal,
  Check,
} from 'lucide-react';
import type { Task, GroupingState, SortingState } from '@/types';
import {
  groupTasksByMultipleLevels,
  flattenGroupedItems,
  getTaskGroupingOptions,
  createDefaultGroupingState,
  toggleGroupCollapse,
  getTaskSortingOptions,
  sortItems,
  createDefaultSortingState,
} from '@/utils/grouping';
import { TaskRow } from './TaskRow';
import { TaskListSkeleton } from './TaskListSkeleton';
import { TaskContextMenu } from './TaskContextMenu';
import {
  ViewToolbar,
  ShowCompletedToggle,
  MultiGroupingSelector,
  MultiSortingSelector,
  MultiFilterSelector,
  ExportButton,
  type BreadcrumbItem,
  type CustomFieldFilterSection,
} from '@/components/shared/view-toolbar';
import clsx from 'clsx';
import { getAllFilterOptions } from '@/utils/filterOptions';
import { useProfilesContext } from '@/context/ProfilesContext';
import { Filter } from 'lucide-react';
import type { Priority } from '@/types';
import { useViewData } from '@/hooks/useViewData';
import { useTaskActions } from '@/hooks/useTaskActions';
import { useDatabase } from '@/context/DatabaseContext';
import { useAppStore } from '@/store/appStore';
import { normalizeStatus, TASK_STATUS } from '@/constants/taskStatuses';
import { useDialogStore } from '@/store/dialogStore';
import { bulkDuplicateTasks } from '@/db/index';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useListColumns } from '@/hooks/useListColumns';
import { fetchCustomFieldsForContext } from '@/db/customFields';
import type { CustomField } from '@/db/customFields';
import { getListSpaceId } from '@/db/projects';
import { fetchTaskStatuses, type TaskStatusDef } from '@/db/taskStatuses';
import { fetchTagsForContext, type TagDefinition } from '@/db/tags';
import { useOrganization } from '@/context/OrganizationContext';
import { useAuth } from '@/context/AuthContext';

// ─── Inline task input rows ───────────────────────────────────────────────────

type FlatListItem = { type: 'header' | 'item' | 'add-subtask-input' | 'add-task-group' | 'group-footer'; data: unknown; key: string; level: number };

function AddSubtaskRow({ level, onConfirm, onCancel }: {
  level: number;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div
      className="flex items-center gap-2 px-2.5 border-b border-border-primary/30 bg-accent-violet/5 h-10"
      style={{ paddingLeft: `${12 + level * 20}px` }}
    >
      {/* Spacers to align with TaskRow: checkbox(w-5) + expand(w-3.5) */}
      <div className="w-5 shrink-0" />
      <div className="w-3.5 shrink-0" />
      <CornerDownRight className="w-3 h-3 text-accent-violet shrink-0" />
      <input
        ref={inputRef}
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); if (name.trim()) { onConfirm(name.trim()); setName(''); } else onCancel(); }
          else if (e.key === 'Escape') onCancel();
        }}
        onBlur={() => { if (!name.trim()) onCancel(); }}
        placeholder="Nuovo subtask… (Invio per creare, Esc per annullare)"
        className="flex-1 text-[12px] text-text-primary bg-transparent outline-none placeholder-text-quaternary"
      />
    </div>
  );
}

function AddGroupTaskRow({ onConfirm, nameColWidth }: {
  onConfirm: (name: string) => void;
  nameColWidth: number;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  if (!editing) {
    return (
      <div
        className="flex items-center h-9 cursor-pointer group/addrow hover:bg-surface-hover/50 transition-colors border-b border-border-primary/20"
        onClick={() => setEditing(true)}
      >
        <div
          className="sticky left-0 z-10 flex items-center gap-1.5 shrink-0 h-full bg-[var(--color-surface-primary,#fff)] group-hover/addrow:bg-surface-hover/90"
          style={{ width: nameColWidth, paddingLeft: 12 }}
        >
          <div className="w-3.5 shrink-0" />
          <div className="w-5 shrink-0" />
          <div className="shrink-0" />
          <Plus className="w-3 h-3 text-text-quaternary group-hover/addrow:text-accent-violet shrink-0 transition-colors" />
          <span className="text-[12px] text-text-quaternary group-hover/addrow:text-text-tertiary transition-colors">Nuovo task…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center h-9 bg-accent-violet/5 border-b border-border-primary/20">
      <div
        className="sticky left-0 z-10 flex items-center gap-1.5 shrink-0 h-full bg-accent-violet/5"
        style={{ width: nameColWidth, paddingLeft: 12 }}
      >
        <div className="w-3.5 shrink-0" />
        <div className="w-5 shrink-0" />
        <div className="shrink-0" />
        <Plus className="w-3 h-3 text-accent-violet shrink-0" />
        <input
          ref={inputRef}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); if (name.trim()) { onConfirm(name.trim()); setName(''); } }
            if (e.key === 'Escape') { setName(''); setEditing(false); }
          }}
          onBlur={() => { if (name.trim()) { onConfirm(name.trim()); } setName(''); setEditing(false); }}
          placeholder="Nome task… (Invio per creare, Esc per annullare)"
          className="flex-1 text-[12px] text-text-primary bg-transparent outline-none placeholder-text-quaternary"
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const statusOrder: Record<string, number> = {
  [TASK_STATUS.IN_PROGRESS]: 1,
  [TASK_STATUS.OPEN]: 2,
  [TASK_STATUS.TODO]: 3,
  [TASK_STATUS.BLOCKED]: 4,
  [TASK_STATUS.COMPLETED]: 5,
  [TASK_STATUS.CANCELLED]: 6,
};

export function TaskList() {
  const {
    loading,
    tasks,
    filters,
    uniqueValues,
    availableAssignees,
    orgMemberNames,
    grouping,
    sorting,
    viewMode,
    currentPath,
    onFilterChange,
    onTaskClick,
    onGroupingChange,
    onSortingChange,
    onNavigate,
    onViewModeChange,
    containersMap,
  } = useViewData();
  const { currentOrg } = useOrganization();
  const selectedTaskId = useAppStore((state) => state.selectedTaskId);
  const [localGrouping, setLocalGrouping] = useState<GroupingState>(() => {
    const initialGrouping = grouping || createDefaultGroupingState();
    // Ensure collapsedGroups is a Set (defensive check for deserialized state)
    if (!(initialGrouping.collapsedGroups instanceof Set)) {
      return {
        ...initialGrouping,
        collapsedGroups: new Set(initialGrouping.collapsedGroups as string[]),
      };
    }
    return initialGrouping;
  });
  const [localSorting, setLocalSorting] = useState<SortingState>(
    () => sorting || createDefaultSortingState()
  );
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'into' | 'after' | null>(null);
  // Local subtask order for in-session reordering (parentId → ordered task IDs)
  const [localSubtaskOrder, setLocalSubtaskOrder] = useState<Map<string, string[]>>(new Map());
  const [addingSubtaskForId, setAddingSubtaskForId] = useState<string | null>(null);
  const { createTask } = useDatabase();
  const openCreateTask = useAppStore((state) => state.openCreateTask);
  const { user } = useAuth();

  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('zenda-expanded-tasks');
      return saved ? new Set<string>(JSON.parse(saved)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  // Sync with global state
  useEffect(() => {
    if (grouping) {
      // Ensure collapsedGroups is a Set (defensive check for deserialized state)
      const safeGrouping = {
        ...grouping,
        collapsedGroups:
          grouping.collapsedGroups instanceof Set
            ? grouping.collapsedGroups
            : new Set(grouping.collapsedGroups as string[]),
      };
      setLocalGrouping(safeGrouping);
    }
  }, [grouping]);

  useEffect(() => {
    if (sorting) setLocalSorting(sorting);
  }, [sorting]);

  // Ensure virtualizer stability when grouping changes
  useEffect(() => {
    // Small delay to allow DOM updates after grouping changes
    const timeoutId = setTimeout(() => {
      // Force virtualizer to recalculate all measurements
      if (parentRef.current) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          // Trigger a minimal scroll to force recalculation
          const currentScroll = parentRef.current?.scrollTop || 0;
          if (parentRef.current) {
            parentRef.current.scrollTop = currentScroll;
          }
        });
      }
    }, 10);
    return () => clearTimeout(timeoutId);
  }, [localGrouping.primary, localGrouping.secondary]);

  const handleGroupingChange = useCallback(
    (newGrouping: GroupingState) => {
      setLocalGrouping(newGrouping);
      onGroupingChange(newGrouping);
    },
    [onGroupingChange]
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setLocalSorting(newSorting);
      onSortingChange(newSorting);
    },
    [onSortingChange]
  );

  const parentRef = useRef<HTMLDivElement>(null);
  const taskUpdateRef = useRef<{
    taskId: string | null;
    timestamp: number;
    scrollOffset: number;
    taskIndex: number;
  }>({
    taskId: null,
    timestamp: 0,
    scrollOffset: 0,
    taskIndex: -1,
  });

  // Track structural changes for virtualizer stability
  const virtualizerKeyRef = useRef<string>('initial');

  const { handleUpdateTask: updateTask, handleDeleteTask: deleteTask } = useTaskActions();
  const dialogConfirm = useDialogStore((s) => s.confirm);

  // Task context menu state
  const [taskContextMenu, setTaskContextMenu] = useState<{
    isOpen: boolean;
    x: number;
    y: number;
    task: Task | null;
  }>({ isOpen: false, x: 0, y: 0, task: null });
  const taskContextMenuRef = useRef<HTMLDivElement>(null);

  const closeTaskContextMenu = useCallback(() => {
    setTaskContextMenu(prev => ({ ...prev, isOpen: false }));
  }, []);

  useClickOutside([taskContextMenuRef], closeTaskContextMenu, taskContextMenu.isOpen);

  // Column picker
  const { visibleColumns, toggleColumn, isVisible, allColumns, getColumnWidth, setColumnWidth } = useListColumns();
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerBtnRef = useRef<HTMLButtonElement>(null);
  const colPickerRef = useRef<HTMLDivElement>(null);
  useClickOutside([colPickerRef, colPickerBtnRef], () => setShowColPicker(false), showColPicker);

  // Custom field columns — resolve from filters.path (set by URL navigation)
  const [customFieldCols, setCustomFieldCols] = useState<CustomField[]>([]);
  // Custom statuses for the current list/project scope
  const [customStatuses, setCustomStatuses] = useState<TaskStatusDef[]>([]);
  // Scoped tags: list, space, org
  const [scopedTags, setScopedTags] = useState<(TagDefinition & { origin: 'org' | 'space' | 'list' })[]>([]);

  useEffect(() => {
    const path = filters.path;
    if (!path) { setCustomFieldCols([]); setCustomStatuses([]); setScopedTags([]); return; }
    let cancelled = false;
    // Path is now ID-based; the last segment is the container ID directly
    const containerId = path.split('/').pop() || null;
    Promise.resolve(containerId).then(async containerId => {
      if (cancelled || !containerId) {
        if (!cancelled) { setCustomFieldCols([]); setCustomStatuses([]); setScopedTags([]); }
        return;
      }
      // Resolve project ID for the list
      const projectId = await getListSpaceId(containerId).catch(() => null);

      // Fetch custom fields, custom statuses, and tags in parallel
      const [fields, listStatuses, tags] = await Promise.all([
        fetchCustomFieldsForContext(containerId, undefined, containerId),
        fetchTaskStatuses('list', containerId).catch(() => [] as TaskStatusDef[]),
        fetchTagsForContext({
          orgId: currentOrg?.id,
          spaceId: projectId || undefined,
          listId: containerId,
        }).catch(() => [] as (TagDefinition & { origin: 'org' | 'space' | 'list' })[]),
      ]);
      if (cancelled) return;
      setCustomFieldCols(fields || []);
      setScopedTags(tags);
      if (listStatuses.length > 0) {
        setCustomStatuses(listStatuses);
      } else if (projectId) {
        const projectStatuses = await fetchTaskStatuses('project', projectId).catch(() => [] as TaskStatusDef[]);
        if (!cancelled) setCustomStatuses(projectStatuses);
      } else {
        setCustomStatuses([]);
      }
    }).catch(() => { if (!cancelled) { setCustomFieldCols([]); setCustomStatuses([]); setScopedTags([]); } });
    return () => { cancelled = true; };
  }, [filters.path, currentOrg?.id]);

  // Compute dynamic min-width based on sticky name column + visible data columns
  const nameColWidth = getColumnWidth('task_name');
  const columnsMinWidth = useMemo(() => {
    let w = nameColWidth;
    for (const col of allColumns) {
      if (col.id !== 'priority' && isVisible(col.id)) w += getColumnWidth(col.id) + 8;
    }
    for (const cf of customFieldCols) {
      if (isVisible(`cf_${cf.id}`)) w += getColumnWidth(`cf_${cf.id}`) + 8;
    }
    w += 40; // column picker button
    return w;
  // isVisible è memoizzata su visibleColumns: elencarlo anche qui sarebbe ridondante
  }, [customFieldCols, isVisible, getColumnWidth, allColumns, nameColWidth]);

  // Column sums for footer
  const calcGroupSums = useCallback((groupTasks: Task[]) => {
    let timeEstimated = 0;
    const customNumberSums: Record<string, number> = {};
    const numberFields = customFieldCols.filter(cf => cf.type === 'number');
    for (const cf of numberFields) customNumberSums[cf.id] = 0;

    for (const t of groupTasks) {
      if (t.timeEstimated) timeEstimated += t.timeEstimated;
      for (const cf of numberFields) {
        const v = t.customFields?.[cf.id];
        if (typeof v === 'number') customNumberSums[cf.id] += v;
        else if (typeof v === 'string' && v !== '') {
          const n = Number(v);
          if (!isNaN(n)) customNumberSums[cf.id] += n;
        }
      }
    }

    return { timeEstimated, customNumberSums, hasAny: timeEstimated > 0 || Object.values(customNumberSums).some(v => v > 0) };
  }, [customFieldCols]);

  // Wheel handler: vertical-only on name area, horizontal-only on columns area
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      const rect = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      if (mouseX > nameColWidth) {
        // Over columns area → convert vertical scroll to horizontal
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          el.scrollLeft += e.deltaY;
        }
      } else {
        // Over name area → only allow vertical scroll
        if (e.deltaX !== 0) {
          e.preventDefault();
          el.scrollTop += e.deltaY;
        }
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [nameColWidth]);

  // Column resize handler
  const handleColumnResize = useCallback((colId: string, startX: number, startWidth: number) => {
    const onMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startX;
      setColumnWidth(colId, startWidth + delta);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [setColumnWidth]);

  // Close task context menu on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeTaskContextMenu();
    };
    if (taskContextMenu.isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [taskContextMenu.isOpen, closeTaskContextMenu]);

  const handleTaskContextMenu = useCallback((e: React.MouseEvent, task: Task) => {
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 300);
    setTaskContextMenu({ isOpen: true, x, y, task });
  }, []);

  const handleStartAddSubtask = useCallback((parentId: string) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      next.add(parentId);
      return next;
    });
    setAddingSubtaskForId(parentId);
  }, []);

  const handleConfirmSubtask = useCallback(async (parentId: string, spaceId: string, name: string) => {
    await createTask({ name, parentId, spaceId, assignees: user?.id ? [user.id] : [] } as Parameters<typeof createTask>[0]);
    // Keep adding more subtasks (user presses Enter again) or close on Escape
  }, [createTask, user?.id]);

  const handleConfirmTask = useCallback(async (name: string, groupOverrides?: Partial<Task>) => {
    const spaceId = tasks[0]?.spaceId;
    if (!spaceId) return;
    await createTask({ name, spaceId, ...groupOverrides } as Parameters<typeof createTask>[0]);
  }, [createTask, tasks]);

  // Wrapper for onUpdateTask that maintains scroll position
  const handleTaskUpdate = useCallback(
    (taskId: string, updates: Partial<Task>) => {
      // Store the task ID being updated and current scroll position
      const currentScrollOffset = parentRef.current?.scrollTop ?? 0;

      taskUpdateRef.current = {
        taskId,
        timestamp: Date.now(),
        scrollOffset: currentScrollOffset,
        taskIndex: -1, // Will be calculated in the effect
      };
      updateTask(taskId, updates);
    },
    [updateTask]
  );

  // Drag & drop handlers for subtask reparenting
  const handleDragStart = useCallback((e: React.DragEvent, taskId: string) => {
    setDraggingId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverId(null);
    setDropPosition(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, taskId: string) => {
    e.preventDefault();
    if (taskId === draggingId) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const threshold = rect.height * 0.28;
    const pos: 'before' | 'into' | 'after' =
      relativeY < threshold ? 'before' : relativeY > rect.height - threshold ? 'after' : 'into';
    setDragOverId(taskId);
    setDropPosition(pos);
    e.dataTransfer.dropEffect = 'move';
  }, [draggingId]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverId(null);
      setDropPosition(null);
    }
  }, []);

  // Organize tasks with subtasks, respecting localSubtaskOrder for reordering
  const { parentTasks, subtasksMap } = useMemo(() => {
    const rawSubtasksMap = new Map<string, Task[]>();
    const rawParentTasks: Task[] = [];

    tasks.forEach((task) => {
      if (task.parentId) {
        const existing = rawSubtasksMap.get(task.parentId) || [];
        existing.push(task);
        rawSubtasksMap.set(task.parentId, existing);
      } else {
        rawParentTasks.push(task);
      }
    });

    // Apply local reordering
    const applyOrder = (items: Task[], groupKey: string): Task[] => {
      const order = localSubtaskOrder.get(groupKey);
      if (!order) return items;
      const byId = new Map(items.map((t) => [t.id, t]));
      const ordered = order.map((id) => byId.get(id)).filter(Boolean) as Task[];
      const missing = items.filter((t) => !order.includes(t.id));
      return [...ordered, ...missing];
    };

    // Sort by sortOrder (if available), falling back to dateCreated
    const sortBySortOrder = (items: Task[]) =>
      [...items].sort((a, b) => {
        const aOrder = a.sortOrder ?? a.dateCreated ?? 0;
        const bOrder = b.sortOrder ?? b.dateCreated ?? 0;
        return aOrder - bOrder;
      });

    const subtasksMap = new Map<string, Task[]>();
    rawSubtasksMap.forEach((children, parentId) => {
      subtasksMap.set(parentId, applyOrder(sortBySortOrder(children), parentId));
    });

    const parentTasks = applyOrder(sortBySortOrder(rawParentTasks), '__root__');

    return { parentTasks, subtasksMap };
  }, [tasks, localSubtaskOrder]);

  const handleDrop = useCallback((e: React.DragEvent, targetTaskId: string) => {
    e.preventDefault();
    const sourceTaskId = e.dataTransfer.getData('text/plain') || draggingId;
    const currentDropPosition = dropPosition;

    setDraggingId(null);
    setDragOverId(null);
    setDropPosition(null);

    if (!sourceTaskId || sourceTaskId === targetTaskId) return;

    // Prevent dropping onto a descendant of the source task
    const getDescendantIds = (taskId: string): Set<string> => {
      const ids = new Set<string>();
      const children = subtasksMap.get(taskId) || [];
      for (const child of children) {
        ids.add(child.id);
        getDescendantIds(child.id).forEach((id) => ids.add(id));
      }
      return ids;
    };
    if (getDescendantIds(sourceTaskId).has(targetTaskId)) return;

    const sourceTask = tasks.find((t) => t.id === sourceTaskId);
    const targetTask = tasks.find((t) => t.id === targetTaskId);
    if (!sourceTask || !targetTask) return;

    if (currentDropPosition === 'into') {
      // Reparent: make sourceTask a child of targetTask
      handleTaskUpdate(sourceTaskId, { parentId: targetTaskId });
      setExpandedTasks((prev) => {
        const next = new Set(prev);
        next.add(targetTaskId);
        try { localStorage.setItem('zenda-expanded-tasks', JSON.stringify([...next])); } catch { /* ignore */ }
        return next;
      });
    } else {
      // Reorder: insert before or after the target at the same level
      const targetParentId = targetTask.parentId ?? null;
      const sourceParentId = sourceTask.parentId ?? null;

      if (sourceParentId !== targetParentId) {
        // Cross-level: reparent to same parent as target, insert before/after
        handleTaskUpdate(sourceTaskId, { parentId: targetParentId });
      }

      // Compute the new order outside of the state setter (no side effects in setState)
      const groupKey = targetParentId ?? '__root__';
      const currentOrder = localSubtaskOrder.get(groupKey)
        ?? (targetParentId
          ? (subtasksMap.get(targetParentId) ?? []).map((t) => t.id)
          : parentTasks.map((t) => t.id));

      const withoutSource = currentOrder.filter((id) => id !== sourceTaskId);
      const targetIndex = withoutSource.indexOf(targetTaskId);
      if (targetIndex !== -1) {
        const insertIndex = currentDropPosition === 'after' ? targetIndex + 1 : targetIndex;
        const reordered = [...withoutSource.slice(0, insertIndex), sourceTaskId, ...withoutSource.slice(insertIndex)];

        // Update local visual order
        setLocalSubtaskOrder((prev) => {
          const next = new Map(prev);
          next.set(groupKey, reordered);
          return next;
        });

        // Persist sort_order for all tasks in the reordered group
        const allTasksById = new Map(tasks.map((t) => [t.id, t]));
        reordered.forEach((taskId, index) => {
          const t = allTasksById.get(taskId);
          const newSortOrder = (index + 1) * 1000;
          if (t && t.sortOrder !== newSortOrder) {
            handleTaskUpdate(taskId, { sortOrder: newSortOrder });
          }
        });
      }
    }
  }, [draggingId, dropPosition, subtasksMap, tasks, parentTasks, handleTaskUpdate, localSubtaskOrder]);

  // Sort tasks with multi-level sorting
  const sortedTasks = useMemo(() => {
    return sortItems(parentTasks, localSorting, (a, b, field, order) => {
      let comparison = 0;

      switch (field) {
        case 'name': {
          comparison = a.name.localeCompare(b.name);
          break;
        }
        case 'status': {
          const statusA = statusOrder[normalizeStatus(a.status)] ?? 99;
          const statusB = statusOrder[normalizeStatus(b.status)] ?? 99;
          comparison = statusA - statusB;
          break;
        }
        case 'dueDate': {
          // Avoid Infinity - Infinity => NaN which makes Array.sort non-deterministic (and causes apparent reordering)
          const hasA = Boolean(a.dueDate);
          const hasB = Boolean(b.dueDate);
          if (!hasA && !hasB) {
            comparison = 0;
          } else if (!hasA && hasB) {
            // No due date should come last in ascending order (and first in descending via order inversion below)
            comparison = 1;
          } else if (hasA && !hasB) {
            comparison = -1;
          } else {
            // Handle both Date objects and number timestamps
            const dateA = a.dueDate as number;
            const dateB = b.dueDate as number;
            comparison = dateA - dateB;
          }
          break;
        }
        case 'dateCreated': {
          // Handle both Date objects and number timestamps
          const createdA = a.dateCreated || 0;
          const createdB = b.dateCreated || 0;
          comparison = createdB - createdA;
          break;
        }
        case 'priority': {
          const priorityOrder = { urgent: 1, high: 2, normal: 3, low: 4 };
          const priorityA = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 5;
          const priorityB = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 5;
          comparison = priorityA - priorityB;
          break;
        }
        case 'assignee': {
          const assigneeA = (a.assignees || [])[0] || '';
          const assigneeB = (b.assignees || [])[0] || '';
          comparison = assigneeA.localeCompare(assigneeB);
          break;
        }
        default:
          comparison = 0;
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [parentTasks, localSorting]);

  // Group tasks with multi-level grouping
  const groupedItems = useMemo(() => {
    return groupTasksByMultipleLevels(sortedTasks, localGrouping);
  }, [sortedTasks, localGrouping]);

  // Flatten for virtual scroll with group headers, then recursively inject expanded subtasks
  const flattenedItems = useMemo((): FlatListItem[] => {
    const base = flattenGroupedItems(groupedItems, localGrouping) as FlatListItem[];

    function injectSubtasks(items: FlatListItem[]): FlatListItem[] {
      if (expandedTasks.size === 0 && !addingSubtaskForId) return items;
      const result: FlatListItem[] = [];
      for (const item of items) {
        result.push(item);
        if (item.type === 'item') {
          const task = item.data as Task;
          if (addingSubtaskForId === task.id) {
            result.push({ type: 'add-subtask-input', data: { parentId: task.id, spaceId: task.spaceId }, key: `add-subtask-${task.id}`, level: (item.level || 0) + 1 });
          }
          if (expandedTasks.has(task.id)) {
            const children = subtasksMap.get(task.id);
            if (children?.length) {
              const childItems: FlatListItem[] = children.map((sub) => ({
                type: 'item' as const,
                data: { ...sub, level: (item.level || 0) + 1 },
                key: `subtask-${sub.id}`,
                level: (item.level || 0) + 1,
              }));
              result.push(...injectSubtasks(childItems));
            }
          }
        }
      }
      return result;
    }

    const withChildren = injectSubtasks(base);

    // Inject persistent add-task rows per group (before each group-footer) or at the end if no grouping
    const hasGroups = withChildren.some(i => i.type === 'group-footer');
    if (hasGroups) {
      const result: FlatListItem[] = [];
      for (const item of withChildren) {
        if (item.type === 'group-footer') {
          const group = item.data as { key: string };
          result.push({ type: 'add-task-group', data: { groupKey: group.key, groupBy: localGrouping.primary }, key: `add-task-${group.key}`, level: 0 });
        }
        result.push(item);
      }
      return result;
    }
    // No grouping: single add-task row at the end
    withChildren.push({ type: 'add-task-group', data: { groupKey: null, groupBy: 'none' }, key: 'add-task-ungrouped', level: 0 });
    return withChildren;
  }, [groupedItems, localGrouping, expandedTasks, subtasksMap, addingSubtaskForId]);

  // Track structural changes for stability
  useMemo(() => {
    const key = `${localGrouping.primary}-${localGrouping.secondary}-${flattenedItems.length}`;
    virtualizerKeyRef.current = key;
  }, [localGrouping.primary, localGrouping.secondary, flattenedItems.length]);

  const handleToggleGroup = useCallback(
    (groupKey: string) => {
      const newGrouping = toggleGroupCollapse(localGrouping, groupKey);
      handleGroupingChange(newGrouping);
    },
    [localGrouping, handleGroupingChange]
  );

  const rowVirtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => parentRef.current,
    getItemKey: (index) => {
      // Ensure stable keys based on task ID or group key
      const item = flattenedItems[index];
      if (!item) return `item-${index}`;
      return item.key;
    },
    estimateSize: (index) => {
      const item = flattenedItems[index];
      if (item?.type === 'header') return 36;
      if (item?.type === 'group-footer') return 32;
      if (item?.type === 'add-task-group') return 32;
      return 42;
    },
    overscan: 20,
    // Measure elements to handle dynamic heights correctly
    measureElement: (element) => element.getBoundingClientRect().height,
  });

  // Maintain scroll position stability during task updates
  useEffect(() => {
    if (!taskUpdateRef.current.taskId || !parentRef.current) return;

    const { timestamp, scrollOffset } = taskUpdateRef.current;
    const ageMs = Date.now() - timestamp;
    // Only process if this is a recent update (within 300ms)
    if (ageMs > 300) {
      taskUpdateRef.current = { taskId: null, timestamp: 0, scrollOffset: 0, taskIndex: -1 };
      return;
    }

    // Simple approach: maintain approximate scroll position
    // Let the virtualizer handle re-positioning naturally
    const delay = 100; // Slightly longer delay to allow DOM updates

    const timeoutId = setTimeout(() => {
      if (parentRef.current) {
        // Restore approximate scroll position to maintain visual stability
        parentRef.current.scrollTop = scrollOffset;

        // Force virtualizer to recalculate positions after DOM updates
        requestAnimationFrame(() => {
          // This ensures the virtualizer accounts for any height changes
          if (parentRef.current) {
            const event = new Event('scroll', { bubbles: false });
            parentRef.current.dispatchEvent(event);
          }
        });
      }

      // Reset
      taskUpdateRef.current = { taskId: null, timestamp: 0, scrollOffset: 0, taskIndex: -1 };
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [flattenedItems]);

  const toggleExpand = useCallback((taskId: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      try {
        localStorage.setItem('zenda-expanded-tasks', JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  const getGroupIcon = (key: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      [TASK_STATUS.IN_PROGRESS]: <Zap className="w-3 h-3 text-accent-blue" />,
      [TASK_STATUS.OPEN]: <CircleDashed className="w-3 h-3 text-text-quaternary" />,
      [TASK_STATUS.TODO]: <CircleDashed className="w-3 h-3 text-text-quaternary" />,
      [TASK_STATUS.BLOCKED]: <ShieldAlert className="w-3 h-3 text-accent-rose" />,
      [TASK_STATUS.COMPLETED]: <Sparkles className="w-3 h-3 text-accent-emerald" />,
      [TASK_STATUS.CANCELLED]: <Archive className="w-3 h-3 text-accent-violet" />,
    };
    return iconMap[key] || <CircleDashed className="w-3 h-3 text-text-quaternary" />;
  };

  // Filter options with consistent ordering
  const { resolveDisplayName } = useProfilesContext();
  const filterOptions = useMemo(() => {
    return getAllFilterOptions(
      uniqueValues.statuses,
      uniqueValues.assignees,
      uniqueValues.tags,
      uniqueValues.priorities as Priority[],
      resolveDisplayName,
      customStatuses,
    );
  }, [uniqueValues, customStatuses, resolveDisplayName]);

  // Build scoped tag groups for filter dropdown
  const tagGroups = useMemo(() => {
    if (scopedTags.length === 0 && uniqueValues.tags.length === 0) return undefined;
    const groups: Array<{ label: string; options: Array<{ value: string; label: string }> }> = [];
    const listTags = scopedTags.filter(t => t.origin === 'list');
    const spaceTags = scopedTags.filter(t => t.origin === 'space');
    const orgTags = scopedTags.filter(t => t.origin === 'org');
    if (listTags.length > 0) groups.push({ label: 'Lista', options: listTags.map(t => ({ value: t.name, label: t.name })) });
    if (spaceTags.length > 0) groups.push({ label: 'Spazio', options: spaceTags.map(t => ({ value: t.name, label: t.name })) });
    if (orgTags.length > 0) groups.push({ label: 'Globali', options: orgTags.map(t => ({ value: t.name, label: t.name })) });
    // Add tags used on tasks that aren't in any scoped group
    const scopedNames = new Set(scopedTags.map(t => t.name));
    const taskOnlyTags = uniqueValues.tags.filter(t => !scopedNames.has(t));
    if (taskOnlyTags.length > 0) groups.push({ label: 'Usati', options: taskOnlyTags.map(t => ({ value: t, label: t })) });
    return groups.length > 0 ? groups : undefined;
  }, [scopedTags, uniqueValues.tags]);

  const allAvailableTags = useMemo(() => {
    const scopedNames = scopedTags.map(t => t.name);
    const all = new Set([...scopedNames, ...uniqueValues.tags]);
    return Array.from(all);
  }, [scopedTags, uniqueValues.tags]);

  // Build custom field filter sections from customFieldCols + task data
  const customFieldFilterSections = useMemo<CustomFieldFilterSection[]>(() => {
    if (customFieldCols.length === 0) return [];
    return customFieldCols
      .map(cf => {
        let options: Array<{ value: string; label: string }>;
        if (cf.type === 'boolean') {
          options = [
            { value: 'true', label: 'Sì' },
            { value: 'false', label: 'No' },
          ];
        } else if (cf.type === 'dropdown' && cf.options && cf.options.length > 0) {
          options = cf.options.map(opt => ({ value: opt.value, label: opt.label }));
        } else {
          // text / date / dropdown without predefined options: extract unique values from tasks
          const values = new Set<string>();
          for (const t of tasks) {
            const val = t.customFields?.[cf.id];
            if (val == null || val === '') continue;
            if (Array.isArray(val)) val.forEach(v => values.add(String(v)));
            else values.add(String(val));
          }
          options = Array.from(values).sort().map(v => {
            if (cf.type === 'date') {
              const d = new Date(v);
              return { value: v, label: isNaN(d.getTime()) ? v : d.toLocaleDateString('it') };
            }
            return { value: v, label: v };
          });
        }
        return { fieldId: cf.id, label: cf.name, type: cf.type, options };
      });
  }, [customFieldCols, tasks]);

  // Helper to resolve an ID segment to a display name using containersMap
  const getNameForId = useCallback((id: string): string => {
    const container = containersMap?.get(id);
    return container?.name ?? id;
  }, [containersMap]);

  // Compute breadcrumbs (exclude current page)
  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [];

    if (!currentPath) {
      return items;
    }

    const pathParts = currentPath.split('/');

    // If we're on a list (3 parts), show space and folder breadcrumbs
    if (pathParts.length === 3) {
      // Space breadcrumb
      items.push({
        label: getNameForId(pathParts[0]),
        onClick: onNavigate ? () => onNavigate(pathParts[0]) : undefined,
      });
      // Folder breadcrumb
      items.push({
        label: getNameForId(pathParts[1]),
        onClick: onNavigate ? () => onNavigate(`${pathParts[0]}/${pathParts[1]}`) : undefined,
      });
    } else if (pathParts.length === 2) {
      // If we're on a folder or list without folder, show only space breadcrumb
      items.push({
        label: getNameForId(pathParts[0]),
        onClick: onNavigate ? () => onNavigate(pathParts[0]) : undefined,
      });
    }
    // If we're on a space (1 part) or root, no breadcrumbs needed

    return items;
  }, [currentPath, onNavigate, getNameForId]);

  // Compute toolbar title and icon based on navigation context
  const { toolbarIcon, toolbarTitle, toolbarSubtitle } = useMemo(() => {
    const taskCount = `${tasks.length} task`;
    if (!currentPath) {
      return {
        toolbarIcon: <ListTodo className="w-4 h-4 text-accent-violet" />,
        toolbarTitle: 'Tutti i task',
        toolbarSubtitle: taskCount,
      };
    }

    const pathParts = currentPath.split('/');
    const lastId = pathParts[pathParts.length - 1];
    const lastPartName = getNameForId(lastId);

    // Determine type based on path length and context
    if (pathParts.length === 3) {
      // Likely a list (space/folder/list)
      return {
        toolbarIcon: <Hash className="w-4 h-4 text-accent-emerald" />,
        toolbarTitle: lastPartName,
        toolbarSubtitle: taskCount,
      };
    } else if (pathParts.length === 2) {
      // Could be folder or list without folder
      return {
        toolbarIcon: <Folder className="w-4 h-4 text-accent-blue" />,
        toolbarTitle: lastPartName,
        toolbarSubtitle: taskCount,
      };
    } else {
      // Single part - space
      return {
        toolbarIcon: <Zap className="w-4 h-4 text-accent-amber" />,
        toolbarTitle: lastPartName,
        toolbarSubtitle: taskCount,
      };
    }
  }, [currentPath, tasks.length, getNameForId]);

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Toolbar */}
      <ViewToolbar
        icon={toolbarIcon}
        title={toolbarTitle}
        subtitle={toolbarSubtitle}
        breadcrumbs={breadcrumbs}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        showViewSwitcher={!!onViewModeChange}
        onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
        leftContent={
          <>
            <MultiFilterSelector
              filters={{
                status: filters.status || [],
                assignees: filters.assignees || [],
                tags: filters.tags || [],
                priority: filters.priority || [],
              }}
              customFieldFilters={filters.customFields}
              customFieldTextFilters={filters.customFieldText}
              customFieldDateRangeFilters={filters.customFieldDateRange}
              onFilterChange={onFilterChange}
              availableOptions={filterOptions}
              customFieldSections={customFieldFilterSections}
              tagGroups={tagGroups}
              icon={<Filter className="w-3.5 h-3.5 text-text-tertiary" />}
            />
            <MultiGroupingSelector
              grouping={localGrouping}
              onGroupingChange={handleGroupingChange}
              availableOptions={getTaskGroupingOptions()}
              icon={<ListTree className="w-3.5 h-3.5 text-text-tertiary" />}
            />
            <MultiSortingSelector
              sorting={localSorting}
              onSortingChange={handleSortingChange}
              availableOptions={getTaskSortingOptions()}
              icon={<ArrowUpDown className="w-3.5 h-3.5 text-text-tertiary" />}
            />
            <ShowCompletedToggle
              showCompleted={filters.showCompleted}
              onToggle={(show) => onFilterChange({ showCompleted: show })}
            />
          </>
        }
        rightContent={
          <div className="flex items-center flex-wrap justify-end gap-2">
            <button
              onClick={openCreateTask}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent-violet text-white text-[11px] font-medium hover:bg-accent-violet/90 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Aggiungi task
            </button>
            <ExportButton tasks={tasks} />
          </div>
        }
      />

      <div ref={parentRef} className="flex-1 min-w-0 overflow-auto">
        <div className="flex flex-col" style={{ minWidth: `${Math.max(680, columnsMinWidth)}px`, minHeight: '100%' }}>
          {/* Column Headers */}
          <div className="group flex items-center gap-2 h-9 border-b border-border-primary bg-surface-secondary/70 text-[10px] uppercase tracking-wider text-text-quaternary font-semibold sticky top-0 z-20">
            {/* Sticky name header */}
            <div className="sticky left-0 z-10 bg-surface-secondary/70 flex items-center gap-2 px-3 shrink-0 relative" style={{ width: nameColWidth }}>
              <div className="w-3.5 shrink-0" />
              <div className="w-5 shrink-0" />
              <div className="w-5 shrink-0" />
              <div className="flex-1 min-w-0 text-left truncate">
                Task
              </div>
              <div
                className="absolute -right-0.5 top-0 bottom-0 w-3 cursor-col-resize z-10 flex items-center justify-center after:content-[''] after:w-px after:h-3/4 after:rounded-full after:bg-border-secondary/60 after:transition-all hover:after:w-[3px] hover:after:bg-accent-violet hover:after:h-full"
                onMouseDown={(e) => { e.preventDefault(); handleColumnResize('task_name', e.clientX, nameColWidth); }}
              />
            </div>
            {/* Dynamic column headers with resize handles */}
            {allColumns.filter(c => c.id !== 'priority' && isVisible(c.id)).map(col => (
              <div key={col.id} className="shrink-0 relative flex items-center text-[10px] uppercase tracking-wider text-text-quaternary font-semibold" style={{ width: getColumnWidth(col.id) }}>
                <span className="truncate">{col.label}</span>
                <div
                  className="absolute -right-0.5 top-0 bottom-0 w-3 cursor-col-resize z-10 flex items-center justify-center after:content-[''] after:w-px after:h-3/4 after:rounded-full after:bg-border-secondary/60 after:transition-all hover:after:w-[3px] hover:after:bg-accent-violet hover:after:h-full"
                  onMouseDown={(e) => { e.preventDefault(); handleColumnResize(col.id, e.clientX, getColumnWidth(col.id)); }}
                />
              </div>
            ))}
            {/* Custom field column headers */}
            {customFieldCols.map(cf => {
              const colId = `cf_${cf.id}`;
              return isVisible(colId) ? (
                <div key={colId} className="shrink-0 relative flex items-center text-[10px] uppercase tracking-wider text-text-quaternary font-semibold" style={{ width: getColumnWidth(colId) }}>
                  <span className="truncate">{cf.name}</span>
                  <div
                    className="absolute -right-0.5 top-0 bottom-0 w-3 cursor-col-resize z-10 flex items-center justify-center after:content-[''] after:w-px after:h-3/4 after:rounded-full after:bg-border-secondary/60 after:transition-all hover:after:w-[3px] hover:after:bg-accent-violet hover:after:h-full"
                    onMouseDown={(e) => { e.preventDefault(); handleColumnResize(colId, e.clientX, getColumnWidth(colId)); }}
                  />
                </div>
              ) : null;
            })}
            {/* Column picker button */}
            <div className="sticky right-0 z-10 bg-surface-secondary/70 pl-2 pr-2 normal-case tracking-normal">
              <button
                ref={colPickerBtnRef}
                onClick={() => setShowColPicker(v => !v)}
                className="p-1 rounded hover:bg-surface-hover text-text-quaternary hover:text-text-tertiary transition-colors"
                title="Configura colonne"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
              </button>
              {showColPicker && (
                <div
                  ref={colPickerRef}
                  className="absolute right-0 top-full mt-1 w-48 bg-surface-primary border border-border-primary rounded-lg shadow-xl z-30 py-1"
                >
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-quaternary font-semibold border-b border-border-primary/50 mb-1">
                    Colonne visibili
                  </div>
                  {allColumns.map(col => (
                    <button
                      key={col.id}
                      onClick={() => toggleColumn(col.id)}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-surface-hover transition-colors text-[12px] text-text-primary"
                    >
                      <div className={clsx('w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                        isVisible(col.id) ? 'bg-accent-violet border-accent-violet' : 'border-border-secondary'
                      )}>
                        {isVisible(col.id) && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      {col.label}
                    </button>
                  ))}
                  {customFieldCols.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-quaternary font-semibold border-t border-border-primary/50 mt-1 pt-2">
                        Campi personalizzati
                      </div>
                      {customFieldCols.map(cf => {
                        const colId = `cf_${cf.id}`;
                        return (
                          <button
                            key={colId}
                            onClick={() => toggleColumn(colId)}
                            className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-surface-hover transition-colors text-[12px] text-text-primary"
                          >
                            <div className={clsx('w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                              isVisible(colId) ? 'bg-accent-violet border-accent-violet' : 'border-border-secondary'
                            )}>
                              {isVisible(colId) && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                            {cf.name}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Virtualized Task List */}
          <div className="flex-1">
            {loading && flattenedItems.length === 0 ? (
              <TaskListSkeleton rows={14} showHeader={false} />
            ) : flattenedItems.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-text-tertiary">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-tertiary flex items-center justify-center">
                    <Folder className="w-8 h-8 text-text-quaternary" />
                  </div>
                  <p className="text-sm font-medium text-text-secondary">Nessun task trovato</p>
                  <p className="text-xs text-text-quaternary mt-1">Prova a modificare i filtri</p>
                </div>
              </div>
            ) : (
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const item = flattenedItems[virtualRow.index];

                  if (item.type === 'header') {
                    const group = item.data as { key: string; label: string; items: Task[] };
                    return (
                      <div
                        key={item.key}
                        ref={rowVirtualizer.measureElement}
                        data-index={virtualRow.index}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <button
                          onClick={() => handleToggleGroup(group.key)}
                          className="sticky left-0 flex items-center gap-2 px-3 py-1.5 bg-surface-secondary/80 hover:bg-surface-secondary transition-colors"
                          style={{ width: nameColWidth }}
                        >
                          <ChevronDown
                            className={clsx(
                              'w-3 h-3 text-text-quaternary transition-transform',
                              localGrouping.collapsedGroups.has(group.key) && '-rotate-90'
                            )}
                          />
                          {getGroupIcon(group.key)}
                          <span className="text-[11px] font-semibold text-text-secondary">
                            {localGrouping.primary === 'assignee' ? resolveDisplayName(group.key) : group.label}
                          </span>
                          <span className="text-[10px] text-text-quaternary tabular-nums font-medium">
                            {group.items.length}
                          </span>
                          <div className="flex-1" />
                          <div className="h-px flex-1 max-w-[200px] bg-linear-to-r from-border-primary/50 to-transparent" />
                        </button>
                      </div>
                    );
                  }

                  // Group footer with subtotals
                  if (item.type === 'group-footer') {
                    const group = item.data as { key: string; label: string; items: Task[] };
                    const sums = calcGroupSums(group.items);
                    if (!sums.hasAny) return null;
                    const formatMs = (ms: number) => {
                      const mins = Math.round(ms / 60000);
                      return mins >= 60 ? `${Math.floor(mins / 60)}h${mins % 60 > 0 ? ` ${mins % 60}m` : ''}` : `${mins}m`;
                    };
                    return (
                      <div
                        key={item.key}
                        ref={rowVirtualizer.measureElement}
                        data-index={virtualRow.index}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                      >
                        <div className="flex items-center h-8 border-b border-border-primary/50 bg-surface-tertiary/30 text-[10px] text-text-quaternary font-medium">
                          <div className="sticky left-0 z-10 bg-surface-tertiary/30 flex items-center px-3 shrink-0" style={{ width: nameColWidth }}>
                            <div className="w-3.5 shrink-0" />
                            <div className="w-5 shrink-0" />
                            <div className="w-5 shrink-0" />
                          </div>
                          {allColumns.filter(c => c.id !== 'priority' && isVisible(c.id)).map(col => {
                            let content: string | null = null;
                            if (col.id === 'time_estimated' && sums.timeEstimated > 0) content = formatMs(sums.timeEstimated);
                            return (
                              <div key={col.id} className="shrink-0 flex items-center" style={{ width: getColumnWidth(col.id) }}>
                                {content && <span className="text-text-tertiary font-semibold">{content}</span>}
                              </div>
                            );
                          })}
                          {customFieldCols.map(cf => {
                            const colId = `cf_${cf.id}`;
                            if (!isVisible(colId)) return null;
                            const sum = cf.type === 'number' ? sums.customNumberSums[cf.id] : undefined;
                            return (
                              <div key={colId} className="shrink-0 flex items-center" style={{ width: getColumnWidth(colId) }}>
                                {sum !== undefined && sum > 0 && <span className="text-text-tertiary font-semibold">{sum.toLocaleString('it-IT')}</span>}
                              </div>
                            );
                          })}
                          <div className="sticky right-0 z-10 bg-surface-tertiary/30 pl-2 pr-2 w-9 shrink-0" />
                        </div>
                      </div>
                    );
                  }

                  // Per-group add-task row (always visible)
                  if (item.type === 'add-task-group') {
                    const { groupKey, groupBy } = item.data as { groupKey: string | null; groupBy: string };
                    return (
                      <div
                        key={item.key}
                        ref={rowVirtualizer.measureElement}
                        data-index={virtualRow.index}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                      >
                        <AddGroupTaskRow
                          nameColWidth={nameColWidth}
                          onConfirm={(name) => {
                            const overrides: Partial<Task> = {};
                            if (groupKey && groupBy !== 'none') {
                              if (groupBy === 'status') overrides.status = groupKey;
                              else if (groupBy === 'priority') overrides.priority = groupKey as Priority;
                              else if (groupBy === 'assignee' && groupKey !== 'non_assegnato') overrides.assignees = [groupKey];
                            }
                            handleConfirmTask(name, overrides);
                          }}
                        />
                      </div>
                    );
                  }

                  // Add-subtask inline input row
                  if (item.type === 'add-subtask-input') {
                    const d = item.data as { parentId: string; spaceId: string };
                    return (
                      <div
                        key={item.key}
                        ref={rowVirtualizer.measureElement}
                        data-index={virtualRow.index}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                      >
                        <AddSubtaskRow
                          level={item.level}
                          onConfirm={(name) => handleConfirmSubtask(d.parentId, d.spaceId, name)}
                          onCancel={() => setAddingSubtaskForId(null)}
                        />
                      </div>
                    );
                  }

                  const taskData = item.data as Task & { level: number };
                  const task = taskData;
                  const subtasks = subtasksMap.get(task.id) || [];
                  const level = item.level;

                  return (
                    <div
                      key={item.key}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualRow.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <TaskRow
                        task={task}
                        subtasks={subtasks}
                        level={level}
                        isExpanded={expandedTasks.has(task.id)}
                        onToggleExpand={() => toggleExpand(task.id)}
                        onTaskClick={onTaskClick}
                        onUpdate={handleTaskUpdate}
                        availableAssignees={availableAssignees}
                        orgMemberNames={orgMemberNames}
                        availableTags={allAvailableTags}
                        isSelected={selectedTaskId === task.id}
                        onContextMenu={handleTaskContextMenu}
                        onAddSubtask={() => handleStartAddSubtask(task.id)}
                        isDragging={draggingId === task.id}
                        isDragOver={dragOverId === task.id && draggingId !== task.id}
                        dropPosition={dragOverId === task.id ? dropPosition : null}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        visibleColumns={visibleColumns}
                        customFieldCols={customFieldCols}
                        customStatuses={customStatuses}
                        getColumnWidth={getColumnWidth}
                        nameColWidth={nameColWidth}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Task Context Menu */}
      {taskContextMenu.isOpen && taskContextMenu.task && (
        <TaskContextMenu
          ref={taskContextMenuRef}
          x={taskContextMenu.x}
          y={taskContextMenu.y}
          task={taskContextMenu.task}
          onStatusChange={(status) => {
            handleTaskUpdate(taskContextMenu.task!.id, { status });
          }}
          onPriorityChange={(priority) => {
            handleTaskUpdate(taskContextMenu.task!.id, { priority });
          }}
          onDuplicate={async () => {
            try {
              await bulkDuplicateTasks([taskContextMenu.task!.id]);
            } catch {
              // Error handled by the function
            }
          }}
          onDelete={async () => {
            const confirmed = await dialogConfirm({
              title: 'Elimina task',
              message: `Sei sicuro di voler eliminare "${taskContextMenu.task!.name}"?`,
              confirmLabel: 'Elimina',
              variant: 'danger',
            });
            if (confirmed) deleteTask(taskContextMenu.task!.id);
          }}
          onClose={closeTaskContextMenu}
        />
      )}
    </div>
  );
}
